import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import FAQ from "@/components/FAQ";
import { motion } from "framer-motion";
import { Lock, KeySquare, DatabaseBackup, ScrollText, Server, ShieldCheck } from "lucide-react";

const PILLARS = [
  { icon: Lock, title: "Encryption everywhere", desc: "Your data is encrypted in transit and at rest." },
  { icon: KeySquare, title: "Role-based access", desc: "Granular permissions for doctors, technicians, drivers, and admins, with session and device management built in." },
  { icon: Server, title: "Cloud-hosted", desc: "Deployed on enterprise-grade infrastructure with region redundancy and DDoS protection." },
  { icon: DatabaseBackup, title: "Daily backups", desc: "Automated snapshots with point-in-time recovery for critical records." },
  { icon: ScrollText, title: "Audit logs", desc: "Every action recorded, with who did it and when." },
  { icon: ShieldCheck, title: "HIPAA-conscious", desc: "Designed with the principles of HIPAA in mind, including BAA-friendly workflows for practices that need one." },
];

const SEC_FAQ = [
  { q: "Are you HIPAA certified?", a: "HIPAA doesn't have a certification body. Rxn3D is designed with HIPAA principles in mind and supports the controls and workflows expected of HIPAA-conscious operations." },
  { q: "Do you sign BAAs?", a: "Business Associate Agreements are available where applicable and required." },
  { q: "Where is data hosted?", a: "In enterprise-grade cloud regions. Regional preference is available on Enterprise plans." },
  { q: "How do we control who sees what?", a: "Granular RBAC with location and role-level scopes. SSO on Enterprise." },
];

export default function Security() {
  return (
    <>
      <PageHero
        chapter="§ 07"
        eyebrow="Security"
        title={"Security, by design"}
        description="Every design decision in Rxn3D is filtered through a security lens, because dental records deserve that level of care."
      />

      <section data-testid="security-pillars" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                data-testid={`security-pillar-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="rounded-3xl hairline glass p-6 lg:p-8"
              >
                <div className="h-11 w-11 rounded-xl hairline inline-flex items-center justify-center mb-6">
                  <Icon className="h-4 w-4 text-brand-plum" />
                </div>
                <h3 className="font-heading text-xl font-semibold">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <FAQ items={SEC_FAQ} title="Security FAQ" />
      <CTA />
    </>
  );
}
